import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';

const PAGE_SIZE = 15;

const toCsvValue = (value) => {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const Payments = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [placeId, setPlaceId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [payments, setPayments] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const response = await api.get('/place/pageNo/1/pageSize/200?search=');
        const all = response.data?.data || [];
        setRestaurants(all.filter(p => p.placeType !== 'DELETED'));
      } catch (err) {
        console.error('Error fetching restaurants:', err);
      }
    };
    fetchRestaurants();
  }, []);

  const buildParams = useCallback((page, pageSize) => {
    const params = new URLSearchParams();
    if (placeId) params.append('placeId', placeId);
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    params.append('pageNo', page);
    params.append('pageSize', pageSize);
    return params.toString();
  }, [placeId, fromDate, toDate]);

  const fetchPayments = useCallback(async (page) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/cashfree/payments?${buildParams(page, PAGE_SIZE)}`);
      const data = response.data?.data || [];
      setPayments(data);
      setTotal(response.data?.total ?? data.length);
    } catch (err) {
      setError(err.response?.data?.responseMsg || err.response?.data?.message || err.message || 'Failed to fetch payments');
      setPayments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchPayments(pageNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo]);

  const applyFilters = () => {
    setPageNo(1);
    fetchPayments(1);
  };

  const clearFilters = () => {
    setPlaceId('');
    setFromDate('');
    setToDate('');
    setPageNo(1);
    // buildParams() closes over state that hasn't updated yet on this tick,
    // so fetch with explicit empty params rather than relying on the stale closure.
    (async () => {
      try {
        setLoading(true);
        const response = await api.get(`/cashfree/payments?pageNo=1&pageSize=${PAGE_SIZE}`);
        setPayments(response.data?.data || []);
        setTotal(response.data?.total ?? 0);
      } catch (err) {
        setError(err.response?.data?.responseMsg || err.message || 'Failed to fetch payments');
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      // Pull every matching record (not just the current page) for the export.
      const response = await api.get(`/cashfree/payments?${buildParams(1, 5000)}`);
      const rows = response.data?.data || [];

      if (rows.length === 0) {
        setError('No payments match the current filters - nothing to export.');
        return;
      }

      const headers = ['Order ID', 'Restaurant', 'Amount', 'Status', 'User ID', 'Created Date'];
      const csvLines = [headers.join(',')];
      rows.forEach(p => {
        csvLines.push([
          toCsvValue(p.orderId),
          toCsvValue(p.placeName),
          toCsvValue(p.amount),
          toCsvValue(p.paymentStatus),
          toCsvValue(p.userId),
          toCsvValue(p.createdDate ? new Date(p.createdDate).toLocaleString() : ''),
        ].join(','));
      });

      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const restaurantLabel = placeId
        ? (restaurants.find(r => String(r.placeId) === String(placeId))?.name || placeId)
        : 'all-restaurants';
      const dateLabel = fromDate || toDate ? `_${fromDate || 'start'}_to_${toDate || 'now'}` : '';
      link.href = url;
      link.download = `payments_${restaurantLabel}${dateLabel}.csv`.replace(/\s+/g, '_');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.responseMsg || err.response?.data?.message || err.message || 'Failed to export payments');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const statusBadge = (status) => {
    const s = (status || '').toUpperCase();
    const colorMap = {
      SUCCESS: 'bg-green-100 text-green-800',
      PAID: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800',
    };
    return colorMap[s] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 ml-64">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
            <p className="mt-2 text-gray-600">View and export payment records across all restaurants.</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col min-w-[240px]">
                <label className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Restaurant</label>
                <select
                  value={placeId}
                  onChange={(e) => setPlaceId(e.target.value)}
                  className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 block w-full p-3"
                >
                  <option value="">All restaurants</option>
                  {restaurants.map((r) => (
                    <option key={r.placeId} value={r.placeId}>{r.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 block p-3"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 block p-3"
                />
              </div>

              <button
                onClick={applyFilters}
                className="bg-[#EB422B] text-white px-4 py-3 rounded-xl text-sm font-medium hover:bg-[#d43a24] transition-colors"
              >
                Apply Filters
              </button>
              <button
                onClick={clearFilters}
                className="bg-gray-100 text-gray-700 px-4 py-3 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="ml-auto bg-green-600 text-white px-4 py-3 rounded-xl text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#EB422B] mx-auto"></div>
                      </td>
                    </tr>
                  ) : payments.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-gray-500">No payments found.</td>
                    </tr>
                  ) : (
                    payments.map((p) => (
                      <tr key={p.cashfreeId || p._id || p.orderId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">{p.orderId}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.placeName || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{p.amount}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge(p.paymentStatus)}`}>
                            {p.paymentStatus || 'UNKNOWN'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.userId}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {p.createdDate ? new Date(p.createdDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {total > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{pageNo}</span> of <span className="font-medium">{totalPages}</span>
                <span className="ml-2">({total} total {total === 1 ? 'payment' : 'payments'})</span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPageNo(p => Math.max(1, p - 1))}
                  disabled={pageNo === 1}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPageNo(p => Math.min(totalPages, p + 1))}
                  disabled={pageNo === totalPages}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payments;
